import { useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import { Building2, Check, ChevronDown } from 'lucide-react';
import type { SessionMembership } from '@kratispot/shared';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';

// A user can belong to several venues; the app works in one at a time. See §4.
export function VenuePicker({
  memberships,
  venueId,
  viaPlatform,
  onPick,
}: {
  memberships: readonly SessionMembership[];
  venueId: string;
  viaPlatform: boolean;
  // The slug, not the id: it is what the address carries. §8
  onPick: (venueSlug: string) => void;
}): ReactElement | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Visible that they are the platform here, and the way back out of it. §3
  const badge = viaPlatform ? (
    <Link
      to="/platform"
      title={t('platformBadgeHint')}
      aria-label={t('platformBadge')}
      className="grid shrink-0 place-items-center rounded-full bg-amber-100 px-1.5 py-1 text-[10px] font-extrabold tracking-wide text-amber-900 hover:bg-amber-200 sm:px-2"
    >
      <Building2 className="size-3.5 sm:hidden" aria-hidden />
      <span className="hidden sm:inline">{t('platformBadge')}</span>
    </Link>
  ) : null;
  if (memberships.length < 2) return badge;
  const current = memberships.find((membership) => membership.venueId === venueId);

  return (
    <>
      {badge}
      <div className="relative min-w-0">
        <button
          type="button"
          aria-label={t('venue')}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen(!open)}
          className="flex h-8 min-w-0 max-w-28 items-center gap-1 rounded-lg border px-2 text-xs font-semibold sm:max-w-44"
        >
          <span className="truncate">{current?.venueName}</span>
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </button>
        {open && (
          <>
            <button
              type="button"
              aria-label={t('close')}
              className="fixed inset-0 z-40 cursor-default"
              onClick={() => setOpen(false)}
            />
            {/* Rows a thumb can hit, and a panel that stays on the screen. §20 */}
            <div
              role="menu"
              className="absolute end-0 z-50 mt-1 max-h-[60vh] w-64 max-w-[85vw] overflow-y-auto rounded-xl border bg-card py-1 shadow-lg"
            >
              {memberships.map((membership) => (
                <button
                  key={membership.venueId}
                  type="button"
                  role="menuitemradio"
                  aria-checked={membership.venueId === venueId}
                  onClick={() => {
                    onPick(membership.venueSlug);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-3 text-start text-sm hover:bg-secondary"
                >
                  <Check
                    aria-hidden
                    className={cn(
                      'size-4 shrink-0 text-primary',
                      membership.venueId === venueId ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{membership.venueName}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
